import { useState, useEffect } from 'react';
import { HowItWorks, IncidentRow, Loading, api } from './_barrel.js';


        // =======================================================================
        // Incidents (all subjects) view — the register
        // =======================================================================
        // Every incident across every subject, newest first, filtered on the
        // server (status, severity, reliability band, source text, subject).
        // Rows are the same expandable IncidentRow the subject page uses, so
        // the analyst can grade, note, acknowledge and sign off from here.
        const GRADE_OPTIONS = [
            ['A', 'A — Reliable'], ['B', 'B — Usually reliable'], ['C', 'C — Fairly reliable'],
            ['D', 'D — Not usually reliable'], ['E', 'E — Unreliable'], ['F', 'F — Cannot be judged'],
        ];
        const STATUS_OPTIONS = ['draft', 'triaged', 'under_review', 'escalated', 'closed', 'false_positive'];
        const SEVERITY_OPTIONS = ['low', 'moderate', 'high', 'critical'];
        const EMPTY_FILTERS = { status: '', severity: '', band: '', subject_id: '', source: '' };

        function IncidentsView({ token, openDialog }) {
            const [data, setData] = useState(null);
            const [subjects, setSubjects] = useState(null);
            const [error, setError] = useState(null);
            const [refresh, setRefresh] = useState(0);
            const [filters, setFilters] = useState(EMPTY_FILTERS);
            // Source is free text: debounce it so each keystroke does not hit the API.
            const [sourceText, setSourceText] = useState('');

            useEffect(() => {
                const t = setTimeout(() => setFilters(f => f.source === sourceText.trim() ? f : { ...f, source: sourceText.trim() }), 300);
                return () => clearTimeout(t);
            }, [sourceText]);

            useEffect(() => {
                (async () => {
                    try {
                        setSubjects(await api('/api/v1/subjects?limit=200', {}, token));
                    } catch (e) { setError(e.message); }
                })();
            }, [token, refresh]);

            useEffect(() => {
                let cancelled = false;
                (async () => {
                    try {
                        const qs = new URLSearchParams({ limit: '200' });
                        Object.entries(filters).forEach(([k, v]) => { if (v) qs.set(k, v); });
                        const i = await api(`/api/v1/incidents?${qs}`, {}, token);
                        if (!cancelled) { setData(i); setError(null); }
                    } catch (e) { if (!cancelled) setError(e.message); }
                })();
                return () => { cancelled = true; };
            }, [token, refresh, filters]);

            const reload = () => setRefresh(r => r + 1);
            const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));
            const clearFilters = () => { setFilters(EMPTY_FILTERS); setSourceText(''); };
            const active = Object.values(filters).some(Boolean);

            const subjectName = (id) => {
                const s = subjects?.items.find(x => x.id === id);
                return s ? (s.display_name || s.full_name) : '—';
            };

            const selectStyle = { minWidth: '150px', padding: '6px 8px', fontSize: '12px' };

            return (
                <div className="max-w-7xl mx-auto px-6 py-10">
                    <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
                        <div>
                            <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2">Incident Register</div>
                            <h2 className="font-display text-4xl gold">All Incidents</h2>
                        </div>
                        <button className="btn-gold" onClick={() => openDialog('new-incident', { onSuccess: reload })}>+ Log incident</button>
                    </div>
                    <HowItWorks id="incidents" title="How the incident register works"
                        intro="Every hostile mention, alert or analyst-logged event across all subjects lands here as an incident — one place to see what needs handling, and how much each source can be trusted."
                        steps={[
                            "Scan the register — newest first. Each row shows the subject, severity, status and the source with its <b>Admiralty grade</b> (A–F): a named source auto-grades <b>B</b>; an incident with <b>no source</b> shows <b>F — cannot be judged</b> until you record one or set the grade yourself.",
                            "Narrow it with the filters — status, severity, grade band, subject, or a word from the source — to work one queue at a time.",
                            "Tap a row to open it: read the detail, <b>override the grade or credibility</b>, add your analyst note, acknowledge it, and <b>sign off</b> a status change.",
                            "Use <b>+ Log incident</b> to record something you learned outside the collectors — pick the subject, name the source, and it starts in draft.",
                            "Close it — or mark a false positive — once handled. Notes you keep flow into the subject's report.",
                        ]} />

                    <div className="flex flex-wrap items-center gap-3 mb-4">
                        <select value={filters.status} onChange={e => setFilter('status', e.target.value)} style={selectStyle} aria-label="Filter by status">
                            <option value="">All statuses</option>
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                        </select>
                        <select value={filters.severity} onChange={e => setFilter('severity', e.target.value)} style={selectStyle} aria-label="Filter by severity">
                            <option value="">All severities</option>
                            {SEVERITY_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={filters.band} onChange={e => setFilter('band', e.target.value)} style={selectStyle} aria-label="Filter by source-reliability grade"
                            title="Effective source-reliability grade: your override if set, otherwise the automatic grade.">
                            <option value="">All grades</option>
                            {GRADE_OPTIONS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                        </select>
                        <select value={filters.subject_id} onChange={e => setFilter('subject_id', e.target.value)} style={{ ...selectStyle, maxWidth: '240px' }} aria-label="Filter by subject">
                            <option value="">All subjects</option>
                            {(subjects?.items || []).map(s => <option key={s.id} value={s.id}>{s.display_name || s.full_name}</option>)}
                        </select>
                        <input value={sourceText} onChange={e => setSourceText(e.target.value)} placeholder="Source contains…" maxLength="200"
                            style={{ ...selectStyle, minWidth: '180px' }} aria-label="Filter by source text" />
                        {active && (
                            <button type="button" className="btn-ghost" onClick={clearFilters} style={{ fontSize: '11px', padding: '5px 12px' }}>Clear filters</button>
                        )}
                        {data && (
                            <span className="text-xs font-mono text-muted">
                                {data.total} incident{data.total === 1 ? '' : 's'}{active ? ' match' : ''}
                            </span>
                        )}
                    </div>

                    {error && <div className="text-[var(--red)] mb-4">{error}</div>}
                    {!data || !subjects ? <Loading /> : data.items.length === 0 ? (
                        <div className="panel p-12 text-center text-muted">
                            {active ? 'No incidents match these filters.' : 'No incidents logged.'}
                        </div>
                    ) : (
                        <div className="panel">
                            <div className="px-6 py-3 border-b border-veil grid grid-cols-12 gap-4 text-xs font-mono uppercase tracking-widest text-muted">
                                <div className="col-span-5">Title · Subject · Grade · Source</div>
                                <div className="col-span-2">Severity</div>
                                <div className="col-span-2">Status</div>
                                <div className="col-span-2">Detected</div>
                                <div className="col-span-1 text-right">Open</div>
                            </div>
                            {data.items.map(i => (
                                <IncidentRow
                                    key={i.id}
                                    incident={i}
                                    token={token}
                                    subjectLabel={subjectName(i.subject_id)}
                                    onSignoff={() => openDialog('signoff', { incident: i, onSuccess: reload })}
                                />
                            ))}
                        </div>
                    )}
                </div>
            );
        }

export { IncidentsView };
